import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

export function getDefaultFlpOverviewPageOneData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'description',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'flp-overview-heading',
              type: 'EducationalSlideHeading',
              props: {
                title: 'What is an FLP?',
                variant: 'h3',
              },
            },
            {
              id: 'flp-overview-body',
              type: 'EditableEducationalSlideBody',
              props: {
                text: 'A family limited partnership is a legal entity where family members pool assets \u2014 business interests, real estate, or investments \u2014 into a partnership governed by a tailored agreement. Senior family members retain full management control while children or trusts hold economic rights with no management authority. For business owners with significant or growing estates, this structure can reduce estate and gift tax exposure, protect assets from creditors, and facilitate a structured transition of wealth \u2014 all within a single partnership structure.',
              },
            },
          ],
        },
      },
      {
        id: 'benefits',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'flp-overview-page-one-columns',
              type: 'TwoColumnLayout',
              props: {
                leftWidth: '50%',
                rightWidth: '50%',
                columnGap: 4,
                showDivider: true,
              },
              children: {
                leftColumn: [
                  {
                    id: 'flp-overview-benefits-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential benefits',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'flp-benefit-retained-control',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Retained control',
                      description:
                        'GPs manage all investment and distribution decisions even after gifting significant economic interests to the next generation.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'flp-benefit-valuation-discounts',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Valuation discounts',
                      description:
                        'LP interests can be transferred at a discount through lack of marketability (LP interests are privately held and hard to sell) and lack of control (minority interests cannot direct distributions or management).',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'flp-benefit-asset-protection',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Asset protection',
                      description:
                        'Partnership assets may be shielded from creditors of individual partners, who generally cannot compel distributions or access FLP assets directly.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'flp-benefit-pass-through-taxation',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Pass-through taxation',
                      description:
                        "Income and gains flow to partners at their individual rates, potentially reducing the family's overall tax burden when LP interests are held by members in lower brackets.",
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                ],
                rightColumn: [
                  {
                    id: 'flp-overview-drawbacks-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential drawbacks',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'flp-drawback-loss-of-economic-benefit',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Loss of economic benefit',
                      description:
                        'Transferring LP interests means giving up cashflow and economic benefit from those assets \u2014 not just management control.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'flp-drawback-liquidity-constraints',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Liquidity constraints',
                      description:
                        'LP holders cannot force distributions, which can limit liquidity when cash is needed.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'flp-drawback-family-dynamics',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Family dynamics',
                      description:
                        'Differences in asset management or distribution decisions can create conflict among family members over time.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'flp-drawback-irs-scrutiny',
                    type: 'PresentationListItem',
                    props: {
                      title: 'IRS scrutiny',
                      description:
                        'FLPs lacking a legitimate non-tax business purpose or where personal and partnership finances are commingled risk having assets pulled back into the taxable estate.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'flp-drawback-administrative-requirements',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Administrative requirements',
                      description:
                        'FLPs must be operated as real business entities \u2014 separate accounts, formal records, and annual tax filings.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  };
}
